// Final Web Speech results have already passed the browser's end-of-phrase
// detector. Keep only the established duplicate-final guard before ordinary
// turn submission; thinking-time replacements finalize immediately.
export const LIVE_FINAL_DEBOUNCE_MS = 20;

// Web Speech's `isFinal` flag closes a recognition segment, not necessarily
// the person's conversational turn. Keep the 20 ms duplicate-result guard,
// then allow a short natural-pause window before committing an ordinary final
// segment. This remains below one second for complete phrases.
// A complete Web Speech final has already crossed the browser's phrase-end
// detector. Retain a short duplicate/continuation opportunity, but do not add
// nearly half a second to every complete warm turn. Connective-ended clauses
// continue to use the separate reflective window below.
export const LIVE_NATURAL_PAUSE_MS = 250;

// A final Web Speech result is a recognition boundary, not proof that a
// person has finished their thought.  Most complete turns settle quickly,
// while clauses ending in a connective receive a slightly longer continuation
// window.  Both public and developer voice use this policy so reflective
// pauses do not become separate chat messages.
export const LIVE_REFLECTIVE_PAUSE_MS = 850;

// If a browser emits interim words but omits both a final result and
// speechend, submit the stable transcript after a bounded quiet window rather
// than leaving the interface in LISTENING until the absolute deadline.
export const LIVE_INTERIM_STALL_MS = 1100;

const CONTINUATION_ENDINGS = new Set([
  'a', 'an', 'and', 'because', 'but', 'for', 'i', "i'm", 'if', 'in', 'of',
  'or', 'so', 'that', 'the', 'then', 'though', 'to', 'unless', 'until',
  'when', 'while', 'with', 'you', 'your',
]);

const REFLECTIVE_CONSTRUCTION_RE = /(?:\bthe\s+reason\b[\s\S]*\bwas|\band\s+another\s+thing|\bi\s+don['’]?t\s+know[,]?\s+maybe)\s*(?:\.{3}|…)?\s*$/i;

export function voiceTurnFinalizationDelay(value, { recognitionBoundary = false } = {}) {
  const normalizedValue = String(value || '').normalize('NFKC');
  const words = normalizedValue
    .toLocaleLowerCase('en-US')
    .replace(/[^\p{L}\p{N}'\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return LIVE_FINAL_DEBOUNCE_MS;
  if (REFLECTIVE_CONSTRUCTION_RE.test(normalizedValue)) return LIVE_REFLECTIVE_PAUSE_MS;
  if (CONTINUATION_ENDINGS.has(words.at(-1))) return LIVE_REFLECTIVE_PAUSE_MS;
  return recognitionBoundary ? LIVE_NATURAL_PAUSE_MS : LIVE_FINAL_DEBOUNCE_MS;
}

// Spoken replies should be complete but conversationally concise. Bounding the
// generation reduces time-to-TTS without forcing sentence fragments.
export const LIVE_RESPONSE_MAX_TOKENS = 48;

// A lost transport response must not strand Live voice in THINKING. The
// server durably commits both sides of the turn before delivery; a cold
// database path can legitimately take longer than the model itself. Keep the
// browser recovery ceiling above that bounded persistence path so a valid
// response is not aborted and discarded moments before it arrives.
export const LIVE_REQUEST_TIMEOUT_MS = 15000;

// Recovery follows trailing silence, not the first detected syllable. Every
// provisional recognition update re-arms this timer while the caller keeps
// speaking, so playback cannot restart over an active interruption.
export const BARGE_IN_FALSE_ALARM_RECOVERY_MS = 360;

// Browsers occasionally emit speechend without a final recognition result.
// Give that final event one brief opportunity to arrive, then deterministically
// accept a credible pending turn or resume the same audio position.
export const BARGE_IN_FINAL_GRACE_MS = 350;

// Raw acoustic activity may arrive without any recognition lifecycle events.
// This fail-safe is re-armed while sound continues and prevents room noise
// from leaving playback paused indefinitely.
export const BARGE_IN_ACOUSTIC_RECOVERY_MS = 700;

// After an acoustic-only false alarm, briefly suppress raw-energy retriggers.
// Web Speech and recorded transcripts remain active during this interval, so
// intentional speech can still interrupt immediately while steady room noise
// cannot create a rapid pause/resume loop.
export const BARGE_IN_ACOUSTIC_COOLDOWN_MS = 800;

// Some browsers deliver the tail of speaker playback as a new recognition
// result immediately after the microphone reopens. Bound the quarantine so a
// later, intentional repetition is still accepted as a new user turn.
export const ASSISTANT_ECHO_COOLDOWN_MS = 2400;

// Absolute fail-safe for a recognition engine that emits speech/interim data
// but never emits either speechend or a final result. Natural pauses remain
// governed by the shorter recovery windows above; this ceiling only prevents
// an indefinitely stuck LISTENING state.
export const VOICE_MAX_UTTERANCE_MS = 30000;
